import { CalendarIcon, PencilIcon, ClockIcon, RefreshIcon, DocumentTextIcon, ExternalLinkIcon, UserGroupIcon } from '@heroicons/react/solid';
import { Link } from 'react-router-dom';

// Short labels keep every board chip the same size; the full name is in the tooltip.
const BOARD_LABELS = {
  NewJobLeads: 'New',
  YouthJobLeads: 'Youth',
  AbleJobLeads: 'Able',
  RefugeeJobLeads: 'Refugee',
  AboriginalJobLeads: 'Aboriginal',
};

const PostAtBtn = ({ website, jobId }) => {
  const url = `https://${website}.ca/jobs/${jobId}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`View on ${website}.ca`}
      className="inline-flex w-[5.5rem] items-center justify-center rounded-md border border-primary px-2 py-1 text-xs font-medium text-primary ease-in duration-100 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary"
    >
      {BOARD_LABELS[website] || website}
      <ExternalLinkIcon className="ml-1 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
    </a>
  );
};

export default function Card({
  jobId,
  title,
  companyName,
  city,
  postedOn,
  postAt,
  postExpiry,
  link,
  recordLink,
  applicantsLink,
  applicantCount,
  jobExpired,
  onExtendClick,
  onRenewClick,
}) {
  // Check if job is within 2 weeks of expiry
  const isWithinTwoWeeks = () => {
    if (!postExpiry) return false;
    const expiryDate = new Date(postExpiry);
    const today = new Date();
    const twoWeeksInMs = 14 * 24 * 60 * 60 * 1000;
    const timeDiff = expiryDate - today;
    return timeDiff > 0 && timeDiff <= twoWeeksInMs;
  };

  const showExtendButton = !jobExpired && onExtendClick && isWithinTwoWeeks();
  const showRenewButton = jobExpired && onRenewClick;

  return (
    <>
      <div className="flex flex-wrap -m-4 ">
        <div className="w-full mb-4 ">
          <div className="border border-gray-200 p-4 rounded-lg bg-gray-100 mb-2 mx-4">
            <div className="lg:flex lg:items-center lg:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-xl font-bold leading-7 text-gray-900">
                    {title}
                  </h2>

                  {/* Extend Button next to title for Active Jobs within 2 weeks of expiry */}
                  {showExtendButton && (
                    <>
                      <span className="text-base font-semibold text-yellow-600">
                        (Expiring Soon)
                      </span>
                      <button
                        onClick={() => onExtendClick({ jobId, title, postedOn, postAt, postExpiry })}
                        className="inline-flex items-center text-base font-bold text-green-600 hover:text-green-700 hover:underline"
                      >
                        <ClockIcon className="h-5 w-5 mr-1.5" aria-hidden="true" />
                        Extend
                      </button>
                    </>
                  )}

                  {/* Renew Button next to title for Expired Jobs */}
                  {showRenewButton && (
                    <button
                      onClick={() => onRenewClick({ jobId, title, postedOn, postAt, postExpiry })}
                      className="inline-flex items-center text-base font-bold text-green-600 hover:text-green-700 hover:underline"
                    >
                      <RefreshIcon className="h-5 w-5 mr-1.5" aria-hidden="true" />
                      Renew
                    </button>
                  )}
                </div>
                {(companyName || city) && (
                  <p className="mt-1 text-sm text-gray-600">
                    {[companyName, city].filter(Boolean).join(' · ')}
                  </p>
                )}
                <div className="mt-1 flex flex-col  sm:flex-row sm:flex-wrap sm:mt-0">
                  <div className="mt-2 flex items-center text-sm text-gray-500 mr-4">
                    <CalendarIcon
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                      aria-hidden="true"
                    />
                    Posted on {postedOn}
                  </div>

                  {/* Show Edit Button Only if the job is active */}
                  {!jobExpired && (
                    <div className="mt-2 flex items-center text-sm text-gray-500 mr-4">
                      <PencilIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />

                      <Link
                        to={link}
                        className="font-bold hover:underline hover:text-primary"
                      >
                        Edit Job Description
                      </Link>
                    </div>
                  )}

                  {/* Applicants (FEAT-APPLY), for active and expired jobs */}
                  {applicantsLink && (
                    <div className="mt-2 flex items-center text-sm text-gray-500 mr-4">
                      <UserGroupIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary" aria-hidden="true" />
                      <Link to={applicantsLink} className="font-bold hover:underline hover:text-primary">
                        Applicants ({applicantCount || 0})
                      </Link>
                    </div>
                  )}

                  {/* Advertisement record (REV-003), for active and expired jobs */}
                  {recordLink && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <DocumentTextIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-primary"
                        aria-hidden="true"
                      />
                      <Link
                        to={recordLink}
                        className="font-bold hover:underline hover:text-primary"
                      >
                        Advertisement Record
                      </Link>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 lg:mt-0 lg:ml-4 lg:flex-shrink-0">
                <p className="mb-1 text-xs font-medium text-gray-500 lg:text-right">View live on</p>
                <div className="flex flex-wrap gap-2 lg:justify-end lg:max-w-[29rem]">
                  {postAt.map((website) => {
                    return (
                      <PostAtBtn key={website} website={website} jobId={jobId} />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
